import { Controller } from '@hotwired/stimulus'
import { stimulus } from '~/init'

const MARK_COLORS = {
  scratch: '#EAB308',
  dent: '#EF4444',
  chip: '#F97316',
  other: '#6B7280'
}

const MARK_RADIUS = 12

class BodyMarksController extends Controller {
  static targets = [
    'svg', 'popup', 'markType', 'markComment', 'markId',
    'photoId', 'photoPreview', 'photoPreviewImg', 'photoGrid',
    'photoUploadInput', 'photoSpinner',
    'lightbox', 'lightboxImg'
  ]

  static values = {
    createUrl: String,
    baseUrl: String,
    marks: { type: Array, default: [] },
    photos: { type: Array, default: [] },
    photosUrl: String,
    csrfToken: String
  }

  connect () {
    this.renderMarkers()
    this._onPhotosDeleted = this.handlePhotosDeleted.bind(this)
    document.addEventListener('photos:deleted', this._onPhotosDeleted)
  }

  disconnect () {
    document.removeEventListener('photos:deleted', this._onPhotosDeleted)
  }

  handlePhotosDeleted (e) {
    const deletedIds = e.detail.ids.map(String)

    // Remove from photos list
    this.photosValue = this.photosValue.filter(p => !deletedIds.includes(String(p.id)))

    // Clear photo references from marks
    const marks = this.marksValue.map(m => {
      if (m.inspection_photo_id && deletedIds.includes(String(m.inspection_photo_id))) {
        return { ...m, inspection_photo_id: null, photo_thumb_url: null, photo_large_url: null }
      }
      return m
    })
    this.marksValue = marks
    this.renderMarkers()
  }

  // ── SVG click → new marker ──

  addMark (e) {
    if (e.target.closest('[data-mark-id]')) return

    const svg = this.svgTarget
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY

    const ctm = svg.getScreenCTM().inverse()
    const svgPt = pt.matrixTransform(ctm)

    const vb = svg.viewBox.baseVal
    const x = ((svgPt.x - vb.x) / vb.width) * 100
    const y = ((svgPt.y - vb.y) / vb.height) * 100

    if (x < 0 || x > 100 || y < 0 || y > 100) return

    this.openPopup({ x, y, mark_type: 'scratch', comment: '', id: null, inspection_photo_id: null, photo_thumb_url: null })
  }

  // ── Click on existing marker ──

  editMark (e) {
    e.stopPropagation()
    const el = e.currentTarget
    const id = el.dataset.markId
    const mark = this.marksValue.find(m => String(m.id) === String(id))
    if (!mark) return

    this.openPopup(mark)
  }

  // ── Popup ──

  openPopup (mark) {
    this.markTypeTarget.value = mark.mark_type || 'scratch'
    this.markCommentTarget.value = mark.comment || ''
    this.markIdTarget.value = mark.id || ''
    this.photoIdTarget.value = mark.inspection_photo_id || ''

    // Store coordinates
    this._pendingX = mark.x
    this._pendingY = mark.y

    // Photo preview
    if (mark.inspection_photo_id && mark.photo_thumb_url) {
      this._selectedLargeUrl = mark.photo_large_url || mark.photo_thumb_url
      this.showPhotoPreview(mark.photo_thumb_url)
    } else {
      this._selectedLargeUrl = null
      this.hidePhotoPreview()
    }

    // Render photo grid
    this.renderPhotoGrid(mark.inspection_photo_id)

    this.popupTarget.classList.remove('hidden')

    // Show/hide delete button
    const deleteBtn = this.popupTarget.querySelector('[data-action*="deleteMark"]')
    if (deleteBtn) {
      deleteBtn.classList.toggle('hidden', !mark.id)
    }
  }

  closePopup () {
    this.popupTarget.classList.add('hidden')
    this._pendingX = null
    this._pendingY = null
    this._selectedLargeUrl = null
    this.hidePhotoPreview()
    this.hidePhotoSpinner()
  }

  stopPropagation (e) {
    e.stopPropagation()
  }

  // ── Photo methods ──

  renderPhotoGrid (selectedId) {
    if (!this.hasPhotoGridTarget) return

    const photos = this.photosValue
    if (photos.length === 0) {
      this.photoGridTarget.classList.add('hidden')
      return
    }

    this.photoGridTarget.classList.remove('hidden')
    const sid = selectedId ? String(selectedId) : null

    this.photoGridTarget.innerHTML = photos.map(p => {
      const isSelected = sid && String(p.id) === sid
      return `<button type="button"
        class="relative rounded-lg overflow-hidden border-2 ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent'} focus:outline-none"
        data-action="click->body-marks#selectPhoto"
        data-photo-id="${p.id}"
        data-photo-url="${p.thumb_url}"
        data-photo-large-url="${p.large_url || p.thumb_url}">
        <img src="${p.thumb_url}" class="w-full h-full object-cover aspect-square" loading="lazy" />
        ${isSelected ? '<div class="absolute inset-0 bg-primary/10"></div>' : ''}
      </button>`
    }).join('')
  }

  selectPhoto (e) {
    e.preventDefault()
    const btn = e.currentTarget
    const photoId = btn.dataset.photoId
    const photoUrl = btn.dataset.photoUrl
    const photoLargeUrl = btn.dataset.photoLargeUrl

    if (this.photoIdTarget.value === photoId) {
      this.removePhoto()
      return
    }

    this.photoIdTarget.value = photoId
    this._selectedLargeUrl = photoLargeUrl || photoUrl
    this.showPhotoPreview(photoUrl)
    this.renderPhotoGrid(photoId)
  }

  removePhoto () {
    this.photoIdTarget.value = ''
    this._selectedLargeUrl = null
    this.hidePhotoPreview()
    this.renderPhotoGrid(null)
  }

  openPhotoUpload () {
    if (this.hasPhotoUploadInputTarget) {
      this.photoUploadInputTarget.click()
    }
  }

  async uploadPhoto () {
    const input = this.photoUploadInputTarget
    if (!input.files || !input.files[0]) return

    this.showPhotoSpinner()

    const formData = new FormData()
    formData.append('inspection_photo[image]', input.files[0])

    try {
      const response = await fetch(this.photosUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': this.csrfTokenValue
        },
        body: formData
      })

      if (!response.ok) {
        const data = await response.json()
        console.error('Photo upload failed:', data.errors)
        return
      }

      const photo = await response.json()

      // Add to photos list
      const photos = [...this.photosValue, photo]
      this.photosValue = photos

      // Auto-select the uploaded photo
      this.photoIdTarget.value = photo.id
      this._selectedLargeUrl = photo.large_url || photo.thumb_url
      this.showPhotoPreview(photo.thumb_url)
      this.renderPhotoGrid(photo.id)
    } catch (err) {
      console.error('Photo upload error:', err)
    } finally {
      this.hidePhotoSpinner()
      input.value = ''
    }
  }

  // ── Lightbox ──

  viewPhoto () {
    const url = this._selectedLargeUrl
    if (url) this.openLightbox(url)
  }

  openLightbox (url) {
    if (!this.hasLightboxTarget) return
    this.lightboxImgTarget.src = url
    this.lightboxTarget.classList.remove('hidden')
  }

  closeLightbox () {
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.add('hidden')
    this.lightboxImgTarget.src = ''
  }

  showPhotoPreview (url) {
    if (!this.hasPhotoPreviewTarget) return
    this.photoPreviewTarget.classList.remove('hidden')
    if (this.hasPhotoPreviewImgTarget) {
      this.photoPreviewImgTarget.src = url
    }
  }

  hidePhotoPreview () {
    if (!this.hasPhotoPreviewTarget) return
    this.photoPreviewTarget.classList.add('hidden')
    if (this.hasPhotoPreviewImgTarget) {
      this.photoPreviewImgTarget.src = ''
    }
  }

  showPhotoSpinner () {
    if (this.hasPhotoSpinnerTarget) {
      this.photoSpinnerTarget.classList.remove('hidden')
    }
  }

  hidePhotoSpinner () {
    if (this.hasPhotoSpinnerTarget) {
      this.photoSpinnerTarget.classList.add('hidden')
    }
  }

  // ── Save (create or update) ──

  async saveMark () {
    const id = this.markIdTarget.value
    const markType = this.markTypeTarget.value
    const comment = this.markCommentTarget.value
    const x = this._pendingX
    const y = this._pendingY
    const inspectionPhotoId = this.photoIdTarget.value || null

    const body = {
      inspection_mark: { x, y, mark_type: markType, comment, inspection_photo_id: inspectionPhotoId }
    }

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': this.csrfTokenValue
    }

    try {
      let response
      if (id) {
        response = await fetch(`${this.baseUrlValue}/${id}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(body)
        })
      } else {
        response = await fetch(this.createUrlValue, {
          method: 'POST',
          headers,
          body: JSON.stringify(body)
        })
      }

      if (!response.ok) {
        const data = await response.json()
        console.error('Save mark failed:', data.errors)
        return
      }

      const savedMark = await response.json()

      // Update local state
      const marks = [...this.marksValue]
      const idx = marks.findIndex(m => String(m.id) === String(savedMark.id))
      if (idx >= 0) {
        marks[idx] = savedMark
      } else {
        marks.push(savedMark)
      }
      this.marksValue = marks

      this.closePopup()
      this.renderMarkers()
    } catch (err) {
      console.error('Save mark error:', err)
    }
  }

  // ── Delete ──

  async deleteMark () {
    const id = this.markIdTarget.value
    if (!id) return

    const headers = {
      Accept: 'application/json',
      'X-CSRF-Token': this.csrfTokenValue
    }

    try {
      const response = await fetch(`${this.baseUrlValue}/${id}`, {
        method: 'DELETE',
        headers
      })

      if (!response.ok && response.status !== 204) {
        console.error('Delete mark failed')
        return
      }

      this.marksValue = this.marksValue.filter(m => String(m.id) !== String(id))
      this.closePopup()
      this.renderMarkers()
    } catch (err) {
      console.error('Delete mark error:', err)
    }
  }

  // ── Render markers on SVG ──

  renderMarkers () {
    const svg = this.svgTarget
    svg.querySelectorAll('[data-mark-id]').forEach(el => el.remove())

    const vb = svg.viewBox.baseVal

    this.marksValue.forEach(mark => {
      const cx = vb.x + (mark.x / 100) * vb.width
      const cy = vb.y + (mark.y / 100) * vb.height
      const color = MARK_COLORS[mark.mark_type] || MARK_COLORS.other

      const ns = 'http://www.w3.org/2000/svg'

      const g = document.createElementNS(ns, 'g')
      g.setAttribute('data-mark-id', mark.id)
      g.setAttribute('data-action', 'click->body-marks#editMark')
      g.style.cursor = 'pointer'

      // Outer ring
      const outer = document.createElementNS(ns, 'circle')
      outer.setAttribute('cx', cx)
      outer.setAttribute('cy', cy)
      outer.setAttribute('r', MARK_RADIUS)
      outer.setAttribute('fill', color)
      outer.setAttribute('fill-opacity', '0.3')
      outer.setAttribute('stroke', color)
      outer.setAttribute('stroke-width', '2')

      // Inner dot
      const inner = document.createElementNS(ns, 'circle')
      inner.setAttribute('cx', cx)
      inner.setAttribute('cy', cy)
      inner.setAttribute('r', 4)
      inner.setAttribute('fill', color)

      g.appendChild(outer)
      g.appendChild(inner)

      // Photo indicator (blue dot)
      if (mark.inspection_photo_id) {
        const indicator = document.createElementNS(ns, 'circle')
        indicator.setAttribute('cx', cx + MARK_RADIUS * 0.7)
        indicator.setAttribute('cy', cy - MARK_RADIUS * 0.7)
        indicator.setAttribute('r', 4)
        indicator.setAttribute('fill', '#3B82F6')
        indicator.setAttribute('stroke', '#ffffff')
        indicator.setAttribute('stroke-width', '1.5')
        g.appendChild(indicator)
      }

      svg.appendChild(g)
    })
  }
}

stimulus.register('body-marks', BodyMarksController)
